import { Fragment, useEffect, useState } from "react";
import { useMediaQuery, InputAdornment, Box } from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { CustomTextField } from "../TracksPageComponents/styles";
import { ProjectSearchBarProps } from "./types";
import CustomButton from "../CustomButton/CustomButton";
import { AISearchDemo } from "../TracksPageComponents/AISearchDemo";

const placeholders = [
  "Search projects...",
  "Try 'jazzy songs'...",
  "Try 'sad piano'...",
  "Try 'chill lofi beats'...",
  "Try 'upbeat pop'...",
  "Try '80s vibe'..."
];

export const ProjectSearchBar: React.FC<ProjectSearchBarProps> = ({
  queryValue,
  setQueryValue,
  setPage,
  setProjects,
}) => {
  const [query, setQuery] = useState<string>("");
  const isSmallScreen = useMediaQuery("(max-width:600px)");

  // Typing animation state
  const [placeholderText, setPlaceholderText] = useState("");
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    let typingSpeed = isDeleting ? 30 : 60;
    
    if (!isDeleting && charIndex === placeholders[placeholderIndex].length) {
      typingSpeed = 2500; // Pause at end of word
      setIsDeleting(true);
    } else if (isDeleting && charIndex === 0) {
      setIsDeleting(false);
      setPlaceholderIndex((prev) => (prev + 1) % placeholders.length);
      typingSpeed = 500; // Pause before next word
    }

    const timer = setTimeout(() => {
      setPlaceholderText(
        placeholders[placeholderIndex].substring(0, charIndex + (isDeleting ? -1 : 1))
      );
      setCharIndex((prev) => prev + (isDeleting ? -1 : 1));
    }, typingSpeed);

    return () => clearTimeout(timer);
  }, [charIndex, isDeleting, placeholderIndex]);

  const escapeRegex = (input: string) => {
    return input.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  };

  const handleQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
    if (e.target.value.trim() === "") {
      setQueryValue("");
      setPage(0);
      setProjects([]);
    }
  };

  useEffect(() => {
    if (queryValue === "") {
      setQuery("");
    }
  }, [queryValue]);

  const handleEnterPress = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" && query.trim() !== "") {
      setQueryValue(escapeRegex(query.trim()));
      setPage(0);
      setProjects([]);
    }
  };

  return (
    <AISearchDemo>
      <Box display="inline-block">
        <CustomTextField
          sx={{
            width: isSmallScreen ? "90vw" : "320px",
            "& .MuiOutlinedInput-root": {
              borderRadius: "24px",
            },
            "& input": {
              color: query.length > 0 ? "#f8f8f8ff" : undefined,
            }
          }}
          value={query}
          placeholder={placeholderText}
          onKeyPress={handleEnterPress}
          size={isSmallScreen ? "small" : "medium"}
          onChange={handleQueryChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <AutoAwesomeIcon sx={{ color: "#4fc3f7", fontSize: 20 }} />
              </InputAdornment>
            ),
            endAdornment: (
              <Fragment>
                <CustomButton
                  onClick={() => {
                    if (query.trim() !== "") {
                      setQueryValue(escapeRegex(query.trim()));
                      setPage(0);
                      setProjects([]);
                    }
                  }}
                  text="Search"
                />
              </Fragment>
            ),
          }}
        />
      </Box>
    </AISearchDemo>
  );
};
